import { Controller } from "@hotwired/stimulus"

// Codes are variable-length (members' real codes from their old system),
// so we no longer auto-submit at a fixed length — the member presses "Check in".
const PIN_MIN_LENGTH = 3
const PIN_MAX_LENGTH = 6

export default class extends Controller {
  static targets = ["dot", "keypad", "status", "dotsContainer", "pinSection", "searchSection", "submit"]
  static values = { checkInsUrl: String, instanceIds: Array, invalidOnLoad: Boolean }

  connect() {
    this.pin = ""
    this.submitting = false
    this.searchMode = false
    this.updateDisplay()
    this.applyMode()
    if (this.hasInvalidOnLoadValue && this.invalidOnLoadValue) {
      this.showInvalidPinWiggle()
    }
  }

  showSearch() {
    this.searchMode = true
    this.applyMode()
    if (this.hasSearchSectionTarget) {
      const input = this.searchSectionTarget.querySelector("input[type='search']")
      if (input) setTimeout(() => input.focus(), 100)
    }
  }

  showPin() {
    this.searchMode = false
    this.applyMode()
  }

  applyMode() {
    if (this.hasPinSectionTarget) {
      this.pinSectionTarget.classList.toggle("hidden", this.searchMode)
      this.pinSectionTarget.classList.toggle("opacity-0", false)
    }
    if (this.hasSearchSectionTarget) {
      this.searchSectionTarget.classList.toggle("hidden", !this.searchMode)
    }
  }

  open() {
    const flow = document.getElementById("kiosk-checkin-flow")
    const search = document.getElementById("kiosk-search-panel")
    const pin = document.getElementById("kiosk-pin-panel")
    if (flow) flow.classList.add("hidden")
    if (search) search.classList.add("hidden")
    if (pin) pin.classList.remove("hidden")
    this.pin = ""
    this.submitting = false
    this.updateDisplay()
  }

  press(event) {
    if (this.submitting) return
    const key = event.currentTarget.dataset.key
    if (key === "⌫") {
      this.pin = this.pin.slice(0, -1)
    } else if (this.pin.length < PIN_MAX_LENGTH) {
      this.pin += String(key)
    }
    this.updateDisplay()
  }

  // Explicit submit (no auto-submit). Requires at least the minimum length.
  submit() {
    if (this.submitting) return
    if (this.pin.length < PIN_MIN_LENGTH) {
      this.setStatus(`Enter at least ${PIN_MIN_LENGTH} digits`)
      return
    }
    this.submitPin()
  }

  clear() {
    if (this.submitting) return
    this.pin = ""
    this.updateDisplay()
  }

  updateDisplay() {
    // One dot per entered digit (variable length). Show a single placeholder
    // dot when empty so the entry area doesn't collapse.
    if (this.hasDotsContainerTarget) {
      const count = this.pin.length
      this.dotsContainerTarget.innerHTML = ""
      if (count === 0) {
        this.dotsContainerTarget.appendChild(this.buildDot(false))
      } else {
        for (let i = 0; i < count; i++) {
          this.dotsContainerTarget.appendChild(this.buildDot(true, i === count - 1))
        }
      }
    }
    if (this.hasSubmitTarget) {
      const ready = this.pin.length >= PIN_MIN_LENGTH
      this.submitTarget.disabled = !ready
      this.submitTarget.classList.toggle("opacity-40", !ready)
      this.submitTarget.classList.toggle("cursor-not-allowed", !ready)
    }
  }

  buildDot(filled, animate = false) {
    const el = document.createElement("span")
    el.className = "inline-flex w-12 h-12 rounded-full border-2 transition-all duration-300 shrink-0 " +
      (filled
        ? "bg-indigo-400 border-indigo-400 shadow-[0_0_16px_-4px_rgba(99,102,241,0.5)]"
        : "bg-transparent border-white/30")
    el.setAttribute("aria-hidden", "true")
    if (animate) {
      el.style.animation = "kiosk-dot-fill 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) forwards"
      el.addEventListener("animationend", () => { el.style.animation = "" }, { once: true })
    }
    return el
  }

  submitPin() {
    if (this.submitting) return
    this.submitting = true
    this.setKeypadDisabled(true)
    this.setStatus("Checking in…")

    const form = document.createElement("form")
    form.method = "POST"
    form.action = this.checkInsUrlValue
    form.style.display = "none"

    const token = document.querySelector("[name='csrf-token']")
    if (token) {
      const input = document.createElement("input")
      input.name = "authenticity_token"
      input.value = token.content
      input.type = "hidden"
      form.appendChild(input)
    }

    const pinInput = document.createElement("input")
    pinInput.name = "pin"
    pinInput.value = this.pin
    pinInput.type = "hidden"
    form.appendChild(pinInput)

    const memberInput = document.createElement("input")
    memberInput.name = "member_id"
    memberInput.value = ""
    memberInput.type = "hidden"
    form.appendChild(memberInput)

    const ids = this.hasInstanceIdsValue ? this.instanceIdsValue : []
    ids.forEach((id) => {
      const input = document.createElement("input")
      input.name = "instance_ids[]"
      input.value = String(id)
      input.type = "hidden"
      form.appendChild(input)
    })

    document.body.appendChild(form)
    form.submit()
  }

  showInvalidPinWiggle() {
    const container = this.hasDotsContainerTarget ? this.dotsContainerTarget : this.element.querySelector("[data-kiosk-pin-target='dotsContainer']") || this.element.querySelector("[data-kiosk-pin-target='dot']")?.parentElement
    if (container) {
      container.style.animation = "kiosk-pin-shake 0.5s ease-in-out"
      container.style.boxShadow = "0 0 24px -6px rgba(239, 68, 68, 0.5)"
      container.addEventListener("animationend", () => {
        container.style.animation = ""
        container.style.boxShadow = ""
      }, { once: true })
    }
    this.pin = ""
    this.submitting = false
    this.setKeypadDisabled(false)
    this.setStatus("Invalid PIN. Try again.")
    this.updateDisplay()
    setTimeout(() => this.setStatus(""), 2000)
  }

  setStatus(text) {
    if (this.hasStatusTarget) this.statusTarget.textContent = text
  }

  setKeypadDisabled(disabled) {
    if (this.hasKeypadTarget) {
      this.keypadTarget.querySelectorAll("button").forEach((btn) => {
        btn.disabled = disabled
      })
    }
  }
}
